/**
 * GEMS Admin - Beta Access Management Page
 * Manage beta testers, invite codes, and monitor capacity
 */
import React, { useState, useEffect } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  Shield, Users, Key, Plus, Trash2, RefreshCw, UserPlus, UserMinus,
  AlertTriangle, Check, X, Copy, Settings, BarChart3, Share2
} from 'lucide-react';

const BetaAccessPage = () => {
  const { api, admin } = useAdmin();
  const [metrics, setMetrics] = useState(null);
  const [testers, setTesters] = useState([]);
  const [codes, setCodes] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showCreateCode, setShowCreateCode] = useState(false);
  const [showGrantAccess, setShowGrantAccess] = useState(false);
  const [newCode, setNewCode] = useState({ code: '', max_uses: 100 });
  const [grantEmail, setGrantEmail] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [newCap, setNewCap] = useState('');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [metricsRes, testersRes, codesRes, referralsRes] = await Promise.all([
        api.get('/api/admin/beta/metrics'),
        api.get('/api/admin/beta/testers'),
        api.get('/api/admin/beta/invite-codes'),
        api.get('/api/admin/beta/referrals'),
      ]);
      setMetrics(metricsRes.data);
      setTesters(testersRes.data.testers || []);
      setCodes(codesRes.data.codes || []);
      setReferrals(referralsRes.data.referrals || []);
    } catch (error) {
      toast.error('Failed to load beta data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCode = async () => {
    if (!newCode.code.trim()) {
      toast.error('Code is required');
      return;
    }
    try {
      await api.post('/api/admin/beta/invite-codes', {
        code: newCode.code.trim().toUpperCase(),
        max_uses: parseInt(newCode.max_uses) || 100,
      });
      toast.success('Invite code created');
      setShowCreateCode(false);
      setNewCode({ code: '', max_uses: 100 });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create code');
    }
  };

  const handleDeleteCode = async (codeId) => {
    if (!window.confirm('Delete this invite code?')) return;
    try {
      await api.delete(`/api/admin/beta/invite-codes/${codeId}`);
      toast.success('Code deleted');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete code');
    }
  };

  const handleToggleCode = async (code) => {
    try {
      await api.put(`/api/admin/beta/invite-codes/${code.id}`, {
        is_active: !code.is_active,
      });
      toast.success(code.is_active ? 'Code disabled' : 'Code enabled');
      fetchAll();
    } catch (error) {
      toast.error('Failed to update code');
    }
  };

  const handleGrantAccess = async () => {
    if (!grantEmail.trim()) return;
    try {
      // First search for user by email
      const searchRes = await api.get(`/api/users/search?q=${encodeURIComponent(grantEmail.trim())}`);
      const users = searchRes.data.users || searchRes.data || [];
      const target = users.find(u => u.email?.toLowerCase() === grantEmail.trim().toLowerCase());
      if (!target) {
        toast.error('User not found with that email');
        return;
      }
      await api.post('/api/admin/beta/grant-access', { user_id: target.id });
      toast.success('Beta access granted');
      setShowGrantAccess(false);
      setGrantEmail('');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant access');
    }
  };

  const handleRevokeAccess = async (userId, userName) => {
    if (!window.confirm(`Revoke beta access from ${userName}?`)) return;
    try {
      await api.post('/api/admin/beta/revoke-access', { user_id: userId });
      toast.success('Beta access revoked');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke access');
    }
  };

  const handleUpdateCap = async () => {
    const cap = parseInt(newCap);
    if (!cap || cap < 1) {
      toast.error('Enter a valid number');
      return;
    }
    try {
      await api.post('/api/admin/beta/update-cap', { new_cap: cap });
      toast.success(`Beta cap updated to ${cap}`);
      setNewCap('');
      fetchAll();
    } catch (error) {
      toast.error('Failed to update cap');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  const filteredTesters = testers.filter(t => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (t.name || '').toLowerCase().includes(s) ||
           (t.email || '').toLowerCase().includes(s) ||
           (t.handle || '').toLowerCase().includes(s);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'testers', label: 'Beta Testers', icon: Users },
    { id: 'codes', label: 'Invite Codes', icon: Key },
    { id: 'referrals', label: 'Referrals', icon: Share2 },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div data-testid="beta-access-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Beta Access Management</h1>
          <p className="text-gray-500 text-sm mt-1">Manage beta testers, invite codes, and monitor capacity</p>
        </div>
        <button
          onClick={fetchAll}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm transition-colors"
          data-testid="beta-refresh-btn"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Capacity Banner */}
      {metrics?.capacity_reached && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center gap-3" data-testid="beta-capacity-warning">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div>
            <p className="font-semibold text-amber-800">Beta tester capacity reached.</p>
            <p className="text-amber-700 text-sm">Invite codes will no longer grant new access. Admins can still manually grant access.</p>
          </div>
        </div>
      )}

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="beta-slots-total">
          <p className="text-sm text-gray-500">Total Beta Slots</p>
          <p className="text-3xl font-bold text-gray-900">{metrics?.beta_cap || 500}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="beta-slots-used">
          <p className="text-sm text-gray-500">Slots Used (excl. AI)</p>
          <p className="text-3xl font-bold text-emerald-600">{metrics?.real_testers || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="beta-slots-remaining">
          <p className="text-sm text-gray-500">Slots Remaining</p>
          <p className="text-3xl font-bold text-blue-600">{metrics?.slots_remaining || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Waitlist</p>
          <p className="text-3xl font-bold text-rose-600">{metrics?.total_waitlist || 0}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-emerald-500 text-emerald-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Beta Access Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">Total beta users (incl. AI/admin)</span>
                <span className="font-medium">{metrics?.total_beta_users || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">Real testers (counting toward cap)</span>
                <span className="font-medium text-emerald-600">{metrics?.real_testers || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">AI / Internal accounts</span>
                <span className="font-medium">{metrics?.ai_internal_accounts || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">Total invite code redemptions</span>
                <span className="font-medium">{metrics?.total_code_redemptions || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-600">Total referral signups</span>
                <span className="font-medium">{metrics?.total_referrals || 0}</span>
              </div>
            </div>
          </div>
          {/* Capacity Progress Bar */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Capacity Usage</h3>
            <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
              <div
                className={`h-4 rounded-full transition-all ${
                  (metrics?.real_testers || 0) >= (metrics?.beta_cap || 500) ? 'bg-red-500' : 'bg-emerald-500'
                }`}
                style={{ width: `${Math.min(100, ((metrics?.real_testers || 0) / (metrics?.beta_cap || 500)) * 100)}%` }}
              />
            </div>
            <p className="text-sm text-gray-500">
              {metrics?.real_testers || 0} / {metrics?.beta_cap || 500} slots used
            </p>
          </div>
        </div>
      )}

      {activeTab === 'testers' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <input
              type="text"
              placeholder="Search testers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm w-64"
              data-testid="beta-testers-search"
            />
            <button
              onClick={() => setShowGrantAccess(true)}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700"
              data-testid="beta-grant-access-btn"
            >
              <UserPlus className="w-4 h-4" /> Grant Access
            </button>
          </div>

          {/* Grant Access Modal */}
          {showGrantAccess && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <h4 className="font-medium text-emerald-800 mb-3">Grant Beta Access</h4>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="Enter user email..."
                  value={grantEmail}
                  onChange={(e) => setGrantEmail(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="beta-grant-email-input"
                />
                <button onClick={handleGrantAccess} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700">
                  Grant
                </button>
                <button onClick={() => setShowGrantAccess(false)} className="px-4 py-2 bg-gray-200 rounded-lg text-sm hover:bg-gray-300">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Testers List */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 text-left text-sm text-gray-600">
                <tr>
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Code Used</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Joined</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTesters.map((tester) => (
                  <tr key={tester.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{tester.name}</p>
                        <p className="text-xs text-gray-500">{tester.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        tester.is_admin ? 'bg-purple-100 text-purple-700' :
                        tester.is_bot ? 'bg-blue-100 text-blue-700' :
                        'bg-emerald-100 text-emerald-700'
                      }`}>
                        {tester.is_admin ? 'Admin' : tester.is_bot ? 'AI/Bot' : tester.beta_role || 'Tester'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{tester.beta_invite_code_used || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{tester.signup_source || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {tester.beta_joined_at ? new Date(tester.beta_joined_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3">
                      {!tester.is_admin && !tester.is_bot && (
                        <button
                          onClick={() => handleRevokeAccess(tester.id, tester.name)}
                          className="text-red-600 hover:text-red-700 text-sm flex items-center gap-1"
                          data-testid={`beta-revoke-${tester.id}`}
                        >
                          <UserMinus className="w-3 h-3" /> Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredTesters.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      No beta testers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'codes' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setShowCreateCode(true)}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700"
              data-testid="beta-create-code-btn"
            >
              <Plus className="w-4 h-4" /> Create Code
            </button>
          </div>

          {showCreateCode && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <h4 className="font-medium text-emerald-800 mb-3">Create Invite Code</h4>
              <div className="flex gap-2 items-end">
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Code</label>
                  <input
                    type="text"
                    placeholder="e.g. BETAROUND2"
                    value={newCode.code}
                    onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-48"
                    data-testid="beta-new-code-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Max Uses</label>
                  <input
                    type="number"
                    value={newCode.max_uses}
                    onChange={(e) => setNewCode({ ...newCode, max_uses: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-24"
                  />
                </div>
                <button onClick={handleCreateCode} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700">
                  Create
                </button>
                <button onClick={() => setShowCreateCode(false)} className="px-4 py-2 bg-gray-200 rounded-lg text-sm hover:bg-gray-300">
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="grid gap-4">
            {codes.map((code) => (
              <div key={code.id} className="bg-white rounded-xl border border-gray-200 p-4" data-testid={`beta-code-${code.code}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Key className={`w-5 h-5 ${code.is_active ? 'text-emerald-500' : 'text-gray-400'}`} />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-lg">{code.code}</span>
                        <button onClick={() => copyCode(code.code)} className="text-gray-400 hover:text-gray-600">
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-sm text-gray-500">
                        {code.current_uses} / {code.max_uses} uses
                        {code.created_by === 'system' && ' (System default)'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      code.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {code.is_active ? 'Active' : 'Disabled'}
                    </span>
                    <button
                      onClick={() => handleToggleCode(code)}
                      className="p-1.5 hover:bg-gray-100 rounded text-gray-500"
                      title={code.is_active ? 'Disable' : 'Enable'}
                    >
                      {code.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleDeleteCode(code.id)}
                      className="p-1.5 hover:bg-red-50 rounded text-red-500"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {/* Usage bar */}
                <div className="mt-3 w-full bg-gray-100 rounded-full h-2">
                  <div
                    className="bg-emerald-500 rounded-full h-2 transition-all"
                    style={{ width: `${Math.min(100, (code.current_uses / code.max_uses) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'referrals' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 text-left text-sm text-gray-600">
              <tr>
                <th className="px-4 py-3">User ID</th>
                <th className="px-4 py-3">Referred By</th>
                <th className="px-4 py-3">Stream ID</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {referrals.map((ref) => (
                <tr key={ref.id} className="border-t border-gray-100">
                  <td className="px-4 py-3 text-sm font-mono">{ref.user_id?.slice(0, 12)}...</td>
                  <td className="px-4 py-3 text-sm font-mono">{ref.referred_by_user_id?.slice(0, 12)}...</td>
                  <td className="px-4 py-3 text-sm">{ref.stream_id || '-'}</td>
                  <td className="px-4 py-3 text-sm">{ref.signup_source}</td>
                  <td className="px-4 py-3 text-sm">{ref.created_at ? new Date(ref.created_at).toLocaleDateString() : '-'}</td>
                </tr>
              ))}
              {referrals.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    No referral signups yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Beta Tester Cap</h3>
            <p className="text-sm text-gray-500 mb-4">
              Current cap: <span className="font-bold text-gray-900">{metrics?.beta_cap || 500}</span> testers.
              AI/internal accounts do not count toward this limit.
            </p>
            <div className="flex gap-2">
              <input
                type="number"
                placeholder="New cap..."
                value={newCap}
                onChange={(e) => setNewCap(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-32"
                data-testid="beta-cap-input"
              />
              <button
                onClick={handleUpdateCap}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700"
                data-testid="beta-update-cap-btn"
              >
                Update Cap
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Admin Instructions</h3>
            <div className="prose prose-sm text-gray-600 space-y-3">
              <p><strong>Creating Invite Codes:</strong> Go to the "Invite Codes" tab and click "Create Code". Set the code name and maximum uses.</p>
              <p><strong>Granting Beta Access Manually:</strong> Go to "Beta Testers" tab, click "Grant Access", enter the user's email address.</p>
              <p><strong>Revoking Beta Access:</strong> Find the user in the "Beta Testers" tab and click "Revoke". Admin and AI accounts cannot be revoked.</p>
              <p><strong>Monitoring Capacity:</strong> The "Overview" tab shows current usage vs. cap. When capacity is reached, a warning banner appears.</p>
              <p><strong>Sending Mobile Download Links:</strong> Share the following links with beta testers:</p>
              <ul className="list-disc pl-5">
                <li>iOS: <code>https://apps.apple.com/app/gems-live/id6736983498</code></li>
                <li>Android: <code>https://play.google.com/store/apps/details?id=com.gemslive.app</code></li>
              </ul>
              <p><strong>Increasing the Beta Cap:</strong> Use the input above to set a new cap. This takes effect immediately.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BetaAccessPage;
